/**
 * Shared API client for analytics-ui. Mirrors the orders-ui client: attaches
 * auth headers, handles 401, and unwraps the StandardApiResponse envelope.
 */

import {
  ApiError,
  getApiHeaders,
  handleUnauthorized,
  type StandardApiResponse,
} from '@lognai/plugin-sdk';
import { userFacingAnalyticsError } from '../lib/user-facing';

const responseErrorMessage = (
  response: { error?: unknown; message?: unknown; details?: unknown },
  status: number
): string => {
  const message = response.error || response.message || `HTTP ${status}`;
  // Connector handlers return sanitized, user-facing diagnostics. The analytics
  // filter otherwise hides words such as PostgreSQL, schema and publication SQL.
  const details = response.details;
  if (
    details &&
    typeof details === 'object' &&
    'type' in details &&
    details.type === 'postgres_connection' &&
    typeof message === 'string' &&
    message.trim()
  ) return message;
  return userFacingAnalyticsError(message);
};

export const apiFetch = async <T>(url: string, options: RequestInit = {}): Promise<T> => {
  const headers = new Headers(getApiHeaders());
  new Headers(options.headers).forEach((value, key) => headers.set(key, value));
  if (typeof FormData !== 'undefined' && options.body instanceof FormData)
    headers.delete('Content-Type');
  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    handleUnauthorized();
    throw new ApiError('Unauthorized', { type: 'unauthorized', status: 401 });
  }

  // The gateway's rate limiter is hit during ordinary tab navigation, and its
  // body reads like any other failure once the status is stripped — so a 429
  // surfaced as "The analysis could not be completed", indistinguishable from a
  // broken query. Name it, and say how long to wait (QA ledger ANLY-006).
  if (response.status === 429) {
    const header = Number(response.headers.get('retry-after'));
    const body = await response.json().catch(() => undefined);
    // Two body shapes exist: the gateway sends details.retryAfter in ms, the
    // api-server sends retryAfter as an ISO timestamp. The header covers both;
    // these are the fallback when a proxy strips it.
    const bodyMs = Number(body?.error?.details?.retryAfter);
    const bodyIso = typeof body?.retryAfter === 'string' ? Date.parse(body.retryAfter) - Date.now() : NaN;
    const seconds = Number.isFinite(header) && header > 0 ? header
      : Number.isFinite(bodyMs) && bodyMs > 0 ? bodyMs / 1000
        : Number.isFinite(bodyIso) && bodyIso > 0 ? bodyIso / 1000
          : undefined;
    const wait = seconds === undefined ? ' Try again in a moment.' : ` Try again in about ${Math.ceil(seconds)}s.`;
    /*
      Says the shared limit, because that is what this is.

      The message used to read "Too many analytics requests in a short time",
      which names a limiter that does not exist: there is no analytics-specific
      limit anywhere in the stack. `generalRateLimiter` is applied globally in
      server.ts at 1000 requests per 15 minutes per IP, and every 429 an
      analytics call receives comes from it.

      The old wording cost real time. It reads as though analytics usage
      specifically caused the lockout and that a stricter analytics threshold
      exists to be tuned, so it sends whoever reads it looking for a
      configuration that is not there (QA ledger GI2-002). It also misdirects
      the reader: the budget is shared with every other request from the same
      address, so the tab that trips it is usually not the tab that spent it.
    */
    throw new ApiError(
      `Too many requests from this address in a short time — this is a shared limit across the whole app, not analytics alone.${wait}`,
      { type: 'rate_limited', status: 429 }
    );
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    throw new ApiError(responseErrorMessage(error, response.status), { ...error.details, status: response.status });
  }

  // A 204 carries no body by definition, and `response.json()` on an empty one
  // throws "Unexpected end of JSON input" — which surfaced a SUCCESSFUL delete
  // as a red error toast (QA ledger PROD-001). Every plugin's client had the
  // same unguarded call.
  //
  // Deliberately narrow: only the statuses that are DEFINED to have no body are
  // special-cased. Reading every response as text first would also cover a 200
  // with an empty body, but that is speculative, and it changes the parse path
  // for every call in the product to fix a case nobody has hit.
  if (response.status === 204 || response.status === 205) return undefined as T;

  const apiResponse: StandardApiResponse<T> = await response.json();
  if (!apiResponse.success) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    throw new ApiError(responseErrorMessage(apiResponse, response.status), { ...(apiResponse as any).details, status: response.status });
  }
  return apiResponse.data;
};

/** POST helper for the analytics query endpoints. */
export const apiPost = <T>(url: string, body: unknown): Promise<T> =>
  apiFetch<T>(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
